import { buildAdminAPI, buildModelService } from "./adminAPI";

export const roleService = buildModelService("/roles");
export const permissionsAPI = buildAdminAPI("/permissions");

function fetchPermissions() {
    return permissionsAPI.get("");
}

export async function buildEditRoleAdminData(roleId) {
    const role = await roleService.fetchById(roleId);
    return buildRoleAdminData(role);
}

export async function buildCreateRoleAdminData() {
    const role = {
        name: "",
        permissions: [],
    };
    return buildRoleAdminData(role);
}

async function buildRoleAdminData(role) {
    const allPermissions = await fetchPermissions();
    const permissionOptions = buildPermissionOptionConfig(role.permissions, allPermissions);

    return {
        ...permissionOptions,
        role,
        allPermissions,
    };
}

export function updatePermissionOptionsConfig(selectedPermissionValues, allPermissionOptions) {
    const allSelectedRolePermissions = selectedPermissionValues.map((selectedPermissionValue) => {
        return mapValueToPermission(selectedPermissionValue);
    });

    const selectedRolePermissions = clearSelectedPermissionsForWildcard(allSelectedRolePermissions);

    return buildPermissionOptionConfig(selectedRolePermissions, allPermissionOptions);
}

export function mapValuesToPermissions(selectedPermissionValues) {
    return selectedPermissionValues.map((selectedPermissionValue) => {
        return mapValueToPermission(selectedPermissionValue);
    });
}

function mapValueToPermission(permissionValue) {
    const [resource, action] = permissionValue.split(":");
    return {
        resource,
        action,
    };
}

function clearSelectedPermissionsForWildcard(allSelectedRolePermissions) {
    const selectedRolePermissions = [];
    for (const selectedPermission of allSelectedRolePermissions) {
        const permissionsForResource = selectedRolePermissions.filter((rolePermission) => {
            return rolePermission.resource === selectedPermission.resource;
        });

        if (selectedPermission.action === "*") {
            // remove non-wildcard permissions for the same resource
            for (const permissionForResource of permissionsForResource) {
                selectedRolePermissions.splice(
                    selectedRolePermissions.indexOf(permissionForResource),
                    1
                );
            }

            selectedRolePermissions.push(selectedPermission);
        } else {
            const hasWildcardForResource =
                permissionsForResource.find((rolePermission) => {
                    return rolePermission.action === "*";
                }) !== undefined;
            if (!hasWildcardForResource) {
                // only add if there isn't already a wildcard for the resource
                selectedRolePermissions.push(selectedPermission);
            }
        }
    }

    return selectedRolePermissions;
}

function comparePermissionsFn(a, b) {
    if (a.permission.resource < b.permission.resource) {
        return -1;
    } else if (a.permission.resource > b.permission.resource) {
        return 1;
    } else {
        // sort wildcard first
        if (a.permission.action === "*") {
            return -1;
        } else if (b.permission.action === "*") {
            return 1;
        }
        return a.permission.action.localeCompare(b.permission.action);
    }
}

export function buildPermissionOptionConfig(rolePermissions, allPermissions) {
    const allPermissionOptionConfigs = [];

    function hasExistingWildcardOptionForResource(resource) {
        return (
            allPermissionOptionConfigs.find((permissionOption) => {
                return (
                    permissionOption.permission.resource === resource &&
                    permissionOption.permission.action === "*"
                );
            }) !== undefined
        );
    }

    for (const permission of allPermissions) {
        if (!hasExistingWildcardOptionForResource(permission.resource)) {
            const anyActionForResourcePermission = {
                resource: permission.resource,
                action: "*",
            };
            const wildcardOption = buildPermissionOption(
                rolePermissions,
                anyActionForResourcePermission
            );
            allPermissionOptionConfigs.push(wildcardOption);
        }

        if (permission.action === "*") {
            // skip wildcard permissions, already added.
            continue;
        }
        const permissionOption = buildPermissionOption(rolePermissions, permission);
        allPermissionOptionConfigs.push(permissionOption);
    }

    const selectedPermissionValues = allPermissionOptionConfigs
        .filter((permissionOption) => permissionOption.selected)
        .map((permissionOption) => permissionOption.value);

    allPermissionOptionConfigs.sort(comparePermissionsFn);

    // remove `selected` attribute - they do not control the checkbox behavior.
    const allPermissionOptions = allPermissionOptionConfigs.map((permissionOption) => {
        const option = { ...permissionOption };
        delete option.selected;
        return option;
    });

    return {
        allPermissionOptions,
        selectedPermissionValues,
    };
}

function buildPermissionOption(rolePermissions, permission) {
    let selected = isSelected(permission, rolePermissions);
    let disabled = isDisabled(permission, rolePermissions);

    const text = `${permission.resource}:${permission.action}`;
    return {
        label: text,
        value: text,
        permission,
        selected,
        disabled,
    };
}

function isDisabled(permission, rolePermissions) {
    return (
        rolePermissions.find((rolePermission) => {
            return (
                rolePermission.resource === permission.resource &&
                rolePermission.action === "*" &&
                permission.action !== "*"
            );
        }) !== undefined
    );
}

function isSelected(permission, rolePermissions) {
    return (
        rolePermissions.find((rolePermission) => {
            return (
                rolePermission.resource === permission.resource &&
                rolePermission.action === permission.action
            );
        }) !== undefined
    );
}
